import { useEffect, useMemo, useState } from "react";
import { AssignmentPanel } from "./AssignmentPanel";
import { PeopleView } from "./PeopleView";
import { ProjectsList } from "./ProjectsList";
import { SkillsPanel } from "./SkillsPanel";
import { RRMatcher } from "./RRMatcher";
import { BeachView } from "./BeachView";
import { OpportunitiesList } from "./OpportunitiesList";
import { useFeatureFlags } from "../contexts/FeatureFlagsContext";

export type DashboardTab = "people" | "beach" | "awsRrs" | "skills" | "assignments" | "rrMatcher" | "opportunities";

const TAB_LABELS: Record<DashboardTab, string> = {
  people: "CoE Pool",
  beach: "Beach",
  awsRrs: "AWS RR's",
  skills: "Skills Catalog",
  assignments: "Assignments",
  rrMatcher: "RR Matcher",
  opportunities: "Opportunities",
};

export const CORE_DASHBOARD_TABS: DashboardTab[] = ["people", "beach", "awsRrs", "skills", "assignments"];
export const ADMIN_DASHBOARD_TABS: DashboardTab[] = [...CORE_DASHBOARD_TABS, "opportunities", "rrMatcher"];

interface DashboardProps {
  allowedTabs: DashboardTab[];
}

export function Dashboard({ allowedTabs }: DashboardProps) {
  const { flags } = useFeatureFlags();

  // Filter tabs based on feature flags
  const filteredTabs = useMemo(() => {
    return allowedTabs.filter((tab) => flags[tab] === true);
  }, [allowedTabs, flags]);

  const safeTabs = useMemo(() => (filteredTabs.length > 0 ? filteredTabs : CORE_DASHBOARD_TABS.filter((tab) => flags[tab] === true)), [filteredTabs, flags]);
  const [activeTab, setActiveTab] = useState<DashboardTab>(safeTabs[0]);

  useEffect(() => {
    if (!safeTabs.includes(activeTab)) {
      setActiveTab(safeTabs[0]);
    }
  }, [activeTab, safeTabs]);

  return (
    <>
      <nav className="tabs">
        {safeTabs.map((tab) => (
          <button
            key={tab}
            type="button"
            className={`tab-button ${tab === activeTab ? "active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {TAB_LABELS[tab]}
          </button>
        ))}
      </nav>

      <section className="tab-content">
        {activeTab === "people" && <PeopleView />}
        {activeTab === "beach" && <BeachView />}
        {activeTab === "awsRrs" && <ProjectsList />}
        {activeTab === "skills" && <SkillsPanel />}
        {activeTab === "assignments" && <AssignmentPanel />}
        {activeTab === "opportunities" && <OpportunitiesList />}
        {activeTab === "rrMatcher" && <RRMatcher />}
      </section>
    </>
  );
}
